/**
 * Canonical lead fields + CSV-header auto-mapping heuristics.
 *
 * Used by the import wizard to:
 *   1) Render the target-column dropdown
 *   2) Auto-suggest a source -> target mapping from raw header names
 *
 * Anything the user leaves mapped to "custom" is stuffed into `leads.custom_fields` (jsonb).
 */

export type LeadTargetField =
  | "skip"
  | "custom"
  | "first_name"
  | "last_name"
  | "full_name"
  | "email"
  | "phone"
  | "phone_alt"
  | "address_street"
  | "address_city"
  | "address_state"
  | "address_zip"
  | "address_combined"
  | "dob"
  | "source"
  | "notes";

interface FieldDef {
  key: LeadTargetField;
  label: string;
  /** Lowercased, non-alnum-stripped patterns that should match a source header. */
  aliases: string[];
}

export const LEAD_FIELDS: FieldDef[] = [
  { key: "skip", label: "— Skip —", aliases: [] },
  { key: "custom", label: "Custom field (custom_fields)", aliases: [] },

  { key: "first_name", label: "First name", aliases: ["firstname", "fname", "first", "givenname"] },
  { key: "last_name", label: "Last name", aliases: ["lastname", "lname", "last", "surname", "familyname"] },
  { key: "full_name", label: "Full name", aliases: ["fullname", "name", "insuredname", "contactname", "leadname"] },

  { key: "email", label: "Email", aliases: ["email", "emailaddress", "mail", "primaryemail"] },
  { key: "phone", label: "Phone", aliases: ["phone", "phonenumber", "primaryphone", "mobile", "cell", "phone1", "telephone", "tel"] },
  { key: "phone_alt", label: "Alt. phone", aliases: ["phone2", "altphone", "alternatephone", "secondaryphone", "homephone", "workphone"] },

  { key: "address_street", label: "Street address", aliases: ["address", "address1", "addressline1", "street", "streetaddress", "addr"] },
  { key: "address_city", label: "City", aliases: ["city", "town", "municipality"] },
  { key: "address_state", label: "State", aliases: ["state", "province", "region", "st"] },
  { key: "address_zip", label: "ZIP / Postal", aliases: ["zip", "zipcode", "postal", "postalcode", "postcode"] },
  { key: "address_combined", label: "Combined address", aliases: ["fulladdress", "combinedaddress", "addressfull"] },

  { key: "dob", label: "Date of birth", aliases: ["dob", "dateofbirth", "birthdate", "birthday"] },
  { key: "source", label: "Lead source", aliases: ["source", "leadsource", "campaign", "channel"] },
  { key: "notes", label: "Notes", aliases: ["notes", "note", "comments", "remarks"] },
];

// `fieldLabel(key)` lookup helper used to live here but had zero consumers
// — call sites read labels off LEAD_FIELDS directly. Removed to keep the
// public surface honest; add back when a real consumer surfaces.

function normalize(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Suggest a target field for each source header. Falls back to "custom". */
export function autoMap(headers: string[]): Record<string, LeadTargetField> {
  const used = new Set<LeadTargetField>();
  const out: Record<string, LeadTargetField> = {};

  for (const header of headers) {
    const norm = normalize(header);
    let chosen: LeadTargetField = "custom";
    for (const f of LEAD_FIELDS) {
      if (f.key === "skip" || f.key === "custom") continue;
      // Singleton fields shouldn't be mapped twice.
      if (used.has(f.key)) continue;
      if (f.aliases.includes(norm)) {
        chosen = f.key;
        used.add(f.key);
        break;
      }
    }
    out[header] = chosen;
  }
  return out;
}

/** Build a Lead row from a parsed CSV record + a header->target mapping. */
export interface BuiltLead {
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  phone_alt: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  address_combined: string | null;
  dob: string | null;
  source: string | null;
  notes: string | null;
  custom_fields: Record<string, string>;
}

const SINGLETON: LeadTargetField[] = [
  "first_name", "last_name", "full_name", "email", "phone", "phone_alt",
  "address_street", "address_city", "address_state", "address_zip",
  "address_combined", "dob", "source", "notes",
];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const US_DATE_RE  = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/;

function normalizeDob(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (ISO_DATE_RE.test(v)) return v;
  const m = US_DATE_RE.exec(v);
  if (m) {
    const [, mo, da, yrRaw] = m;
    const yr = yrRaw.length === 2 ? (Number(yrRaw) >= 50 ? "19" : "20") + yrRaw : yrRaw;
    const mm = mo.padStart(2, "0");
    const dd = da.padStart(2, "0");
    return `${yr}-${mm}-${dd}`;
  }
  return null;
}

export function buildLead(
  row: Record<string, string>,
  mapping: Record<string, LeadTargetField>,
): BuiltLead {
  const lead: BuiltLead = {
    first_name: null, last_name: null, full_name: null,
    email: null, phone: null, phone_alt: null,
    address_street: null, address_city: null, address_state: null,
    address_zip: null, address_combined: null,
    dob: null, source: null, notes: null,
    custom_fields: {},
  };

  for (const [header, target] of Object.entries(mapping)) {
    if (target === "skip") continue;
    const raw = (row[header] ?? "").toString().trim();
    if (!raw) continue;

    if (target === "custom") {
      lead.custom_fields[header] = raw;
      continue;
    }
    if (target === "dob") {
      lead.dob = normalizeDob(raw);
      continue;
    }
    if (target === "address_state") {
      // Uppercase so "OH", "oh", " Oh " all collapse into the same
      // distinct-value in the State filter dropdown.
      lead.address_state = raw.toUpperCase();
      continue;
    }
    if (SINGLETON.includes(target)) {
      // Type-safe assignment via a narrowed key.
      (lead as unknown as Record<string, string | null>)[target] = raw;
    }
  }

  return lead;
}
